import classNames from 'classnames'
import type { FunctionComponent, PropsWithChildren } from 'react'
import { useState } from 'react'

import styles from './mobile-comparison-table.module.scss'
import { RawAnchor } from '@components/anchor'
import { ExpandingPlusButton } from '@components/expanding-plus-button'
import { Check } from '@components/icons/check'
import { Close } from '@components/icons/close'
import { Image } from '@components/image'
import { Markdown } from '@components/markdown'
import { Mobile } from '@components/responsive'
import { Text } from '@components/text'
import { ActionButton } from '@contentful-components/action-button'
import type {
    ComparisonTableFragment,
    PlanFragment,
} from '@graphql/fragments/ComparisonTableFragment.graphql'
import { usePersonalizations } from '@lib/hooks/usePersonalizations'
import { renderTemplate, TEXT } from '@lib/utils/text'

export const MobileComaprisonTable: FunctionComponent<
    Omit<ComparisonTableFragment, 'type' | 'sys'>
> = ({ title, plans }) => (
    <Mobile>
        <Text
            tag='h2'
            typeStyle='large-prompt'
            bold
            className={styles['title']}
        >
            {title}
        </Text>
        <PlanList plans={plans} />
    </Mobile>
)

const PlanList: FunctionComponent<{
    plans: ComparisonTableFragment['plans']
}> = ({ plans }) => (
    <div className={styles['plan-list']}>
        {plans?.items.map((plan) => (
            <div key={plan.sys.id}>
                <PlanCard {...plan} alwaysOpen={plans?.items?.length === 1}>
                    <PlanBenefits benefits={plan?.benefits} />
                </PlanCard>
                {!!plan?.button && (
                    <div className={styles['cta-button']}>
                        <ActionButton
                            preset={plan?.button?.preset}
                            {...plan?.button}
                        />
                    </div>
                )}
            </div>
        ))}
    </div>
)

const PlanCard: FunctionComponent<
    PropsWithChildren<PlanFragment & { alwaysOpen?: boolean }>
> = ({ title, description, image, theme, children, alwaysOpen = false }) => {
    const [isExpanded, setIsExpanded] = useState(alwaysOpen)

    const handleExpandClick = (e: React.MouseEvent) => {
        if (alwaysOpen) return

        e.stopPropagation()

        setIsExpanded(!isExpanded)
    }

    if (!title) return null

    return (
        <div
            className={classNames(
                styles['plan-card'],
                styles[`theme-${theme}`],
            )}
            onClick={handleExpandClick}
        >
            {image?.source && (
                <Image
                    source={image.source}
                    className={styles['plan-image']}
                    alt={image.alt}
                />
            )}

            <div className={styles['plan-toggle-row']}>
                <Text
                    tag='h3'
                    typeStyle='large-prompt'
                    className={styles['plan-title']}
                    bold
                >
                    {title}
                </Text>
                {description && (
                    <Text
                        tag='div'
                        typeStyle='body-xs'
                        className={styles['plan-description']}
                    >
                        {description}
                    </Text>
                )}
                {!alwaysOpen && (
                    <ExpandingPlusButton
                        className={styles['plan-toggle']}
                        open={isExpanded}
                        onClick={handleExpandClick}
                    />
                )}
            </div>

            {isExpanded && <div>{children}</div>}
        </div>
    )
}

const PlanBenefits: FunctionComponent<{
    benefits: PlanFragment['benefits']
}> = ({ benefits }) => {
    const personalizations = usePersonalizations()

    return (
        <div className={styles['plan-benefits']}>
            {benefits?.items.map((item) => {
                const title =
                    item.value !== '-'
                        ? `${item.benefit?.title}, ${item.value}`
                        : item.benefit?.title

                return (
                    <div key={item?.sys?.id} className={styles['plan-benefit']}>
                        <div className={styles['plan-benefit-text']}>
                            <Markdown
                                tag='h4'
                                typeStyle='body-20'
                                markdown={renderTemplate(
                                    title,
                                    personalizations,
                                )}
                                color='white'
                            />
                            {item.benefit?.description && (
                                <Markdown
                                    typeStyle={{ _: 'body-lg', md: 'body-md' }}
                                    markdown={renderTemplate(
                                        item.benefit?.description,
                                        personalizations,
                                    )}
                                    color='white'
                                />
                            )}
                            {item?.benefit?.link && (
                                <RawAnchor
                                    href={item?.benefit?.link}
                                    preset='plain'
                                    className={styles.link}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {TEXT.learnMore}
                                </RawAnchor>
                            )}
                        </div>
                        <div className={styles['plan-benefit-icon']}>
                            {item.value !== '-' && <Check width='24' />}
                            {item.value === '-' && <Close width='24' />}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
